import type { DagDirection, DagSummaryGroup, DagViewMode } from "./viewProjections";
import { abbreviatedSnapshotToken } from "./temporalState";

interface DagViewDefinition {
  id: DagViewMode;
  label: string;
  description: string;
  glyph: string;
  group: "Explore" | "Analyse" | "History";
}

export interface DagImpactSummary {
  reachable: number;
  propagationEnabled: number;
  explicitlyInvalidated: number;
  potentialOnly: number;
  stopped: number;
  indeterminate: number;
}

export const DAG_VIEW_DEFINITIONS: DagViewDefinition[] = [
  { id: "chronicle", label: "Chronicle", description: "Time × categories", glyph: "◷", group: "Explore" },
  { id: "flow", label: "Flow", description: "Inputs to outcomes", glyph: "→", group: "Explore" },
  { id: "focus", label: "Focus", description: "Local neighbourhood", glyph: "◎", group: "Explore" },
  { id: "summary", label: "Summary", description: "Grouped overview", glyph: "⌁", group: "Explore" },
  { id: "dsm", label: "DSM", description: "Hierarchical coupling matrix", glyph: "▦", group: "Analyse" },
  { id: "impact", label: "Impact", description: "Typed invalidation paths", glyph: "↯", group: "Analyse" },
  { id: "compare", label: "Ancestry Compare", description: "Shared vs divergent lineage", glyph: "⇄", group: "Analyse" },
  { id: "time_compare", label: "Time Compare", description: "Changes across two instants", glyph: "◴", group: "Analyse" },
  { id: "runs", label: "Runs", description: "Families × revisions", glyph: "▦", group: "History" },
  { id: "audit", label: "Audit", description: "Recorded history", glyph: "≡", group: "History" },
];

const GROUPS = ["Explore", "Analyse", "History"] as const;

export function DagViewSwitcher({
  mode,
  onChange,
}: {
  mode: DagViewMode;
  onChange: (mode: DagViewMode) => void;
}) {
  return (
    <nav className="dag-viewer__view-switcher" aria-label="Lineage views" data-testid="dag-view-switcher">
      {GROUPS.map((group) => (
        <section key={group} aria-label={group}>
          <span>{group}</span>
          <div>
            {DAG_VIEW_DEFINITIONS.filter((view) => view.group === group).map((view) => (
              <button
                type="button"
                key={view.id}
                className={mode === view.id ? "is-active" : ""}
                aria-pressed={mode === view.id}
                aria-label={`${view.label}: ${view.description}`}
                data-testid={`dag-view-mode-${view.id}`}
                onClick={() => onChange(view.id)}
              >
                <i aria-hidden="true">{view.glyph}</i>
                <span><strong>{view.label}</strong><small>{view.description}</small></span>
              </button>
            ))}
          </div>
        </section>
      ))}
    </nav>
  );
}

const DIRECTION_OPTIONS: Array<{ value: DagDirection; label: string }> = [
  { value: "upstream", label: "Upstream" },
  { value: "both", label: "Both" },
  { value: "downstream", label: "Downstream" },
];

const DEPTH_OPTIONS: Array<{ value: number | null; label: string }> = [
  { value: 1, label: "1 hop" },
  { value: 2, label: "2 hops" },
  { value: 3, label: "3 hops" },
  { value: null, label: "All" },
];

const SUMMARY_GROUPS: Array<{ value: DagSummaryGroup; label: string }> = [
  { value: "category", label: "Category" },
  { value: "kind", label: "Kind" },
  { value: "type", label: "Record type" },
  { value: "state", label: "State" },
  { value: "actor", label: "Actor" },
];

export function DagContextBar({
  mode,
  selectedLabel,
  direction,
  depth,
  compareALabel,
  compareBLabel,
  timeCompareBeforeInput,
  timeCompareAfterInput,
  timeCompareError,
  timeCompareReady = false,
  timeCompareRecordedAsOf,
  timeCompareSnapshotToken,
  summaryGroup,
  serverBacked = false,
  boundaryCount = 0,
  resultCount,
  impactSummary,
  impactEffectiveAsOf,
  impactComplete,
  onDirection,
  onDepth,
  onSummaryGroup,
  onSwapCompare,
  onClearCompare,
  onTimeCompareBefore,
  onTimeCompareAfter,
}: {
  mode: DagViewMode;
  selectedLabel?: string | null;
  direction: DagDirection;
  depth: number | null;
  compareALabel?: string | null;
  compareBLabel?: string | null;
  timeCompareBeforeInput?: string;
  timeCompareAfterInput?: string;
  timeCompareError?: string | null;
  timeCompareReady?: boolean;
  timeCompareRecordedAsOf?: string | null;
  timeCompareSnapshotToken?: string | null;
  summaryGroup: DagSummaryGroup;
  serverBacked?: boolean;
  boundaryCount?: number;
  resultCount?: number;
  impactSummary?: DagImpactSummary | null;
  impactEffectiveAsOf?: string | null;
  impactComplete?: boolean;
  onDirection: (direction: DagDirection) => void;
  onDepth: (depth: number | null) => void;
  onSummaryGroup: (group: DagSummaryGroup) => void;
  onSwapCompare: () => void;
  onClearCompare: () => void;
  onTimeCompareBefore: (value: string) => void;
  onTimeCompareAfter: (value: string) => void;
}) {
  if (mode === "chronicle") {
    return <div className="dag-viewer__context-bar"><p><strong>Chronicle</strong> Time runs left to right; categories remain independently reorderable and hideable.</p></div>;
  }
  if (mode === "flow") {
    return <div className="dag-viewer__context-bar"><p><strong>Flow</strong> Structural stages run from inputs to outcomes. Timestamps do not influence placement.</p></div>;
  }
  if (mode === "summary") {
    return (
      <div className="dag-viewer__context-bar">
        <p><strong>Summary</strong> Relationship width represents record-edge count, not numerical flow.</p>
        <label>Group by
          <select value={summaryGroup} onChange={(event) => onSummaryGroup(event.target.value as DagSummaryGroup)}>
            {SUMMARY_GROUPS
              .filter((option) => !serverBacked || option.value === "category" || option.value === "state")
              .map((option) => <option key={option.value} value={option.value}>{option.label}</option>)}
          </select>
        </label>
      </div>
    );
  }
  if (mode === "dsm") {
    return <div className="dag-viewer__context-bar"><p><strong>DSM</strong> Server-owned bands expose bounded coupling without loading the whole graph. Dependency and model context remain separate evidence modes.</p></div>;
  }
  if (mode === "compare") {
    return (
      <div className="dag-viewer__context-bar is-compare">
        <p><strong>Ancestry compare</strong> Select two records. Shared ancestry stays central; exclusive branches split around it.</p>
        <div className="dag-viewer__compare-tray" aria-label="Ancestry comparison records">
          <span><i>A</i>{compareALabel ?? "Select the first record"}</span>
          <span><i>B</i>{compareBLabel ?? "Select the second record"}</span>
          <button type="button" disabled={!compareALabel || !compareBLabel} onClick={onSwapCompare}>Swap</button>
          <button type="button" disabled={!compareALabel && !compareBLabel} onClick={onClearCompare}>Clear</button>
        </div>
        <div className="dag-viewer__segmented" role="radiogroup" aria-label="Ancestry comparison depth">
          {DEPTH_OPTIONS.map((option) => (
            <button type="button" role="radio" aria-checked={depth === option.value} className={depth === option.value ? "is-active" : ""} key={option.label} onClick={() => onDepth(option.value)}>{option.label}</button>
          ))}
        </div>
      </div>
    );
  }
  if (mode === "time_compare") {
    return (
      <div className="dag-viewer__context-bar is-time-compare">
        <p>
          <strong>Time Compare</strong>
          {selectedLabel ? <> Changes for <b>{selectedLabel}</b> across two effective/event instants.</> : " Select one ArtifactVersion to compare."}
        </p>
        <div className="dag-viewer__time-compare-window" aria-label="Time Compare window">
          <label>
            <span>Before effective / event time</span>
            <input
              type="text"
              value={timeCompareBeforeInput ?? ""}
              onChange={(event) => onTimeCompareBefore(event.target.value)}
              placeholder="2026-07-01T12:00:00Z"
              spellCheck={false}
            />
          </label>
          <label>
            <span>After effective / event time</span>
            <input
              type="text"
              value={timeCompareAfterInput ?? ""}
              onChange={(event) => onTimeCompareAfter(event.target.value)}
              placeholder="Active effective cut"
              spellCheck={false}
            />
          </label>
        </div>
        <div className="dag-viewer__segmented" role="radiogroup" aria-label="Time Compare direction">
          {DIRECTION_OPTIONS.map((option) => (
            <button type="button" role="radio" aria-checked={direction === option.value} className={direction === option.value ? "is-active" : ""} key={option.value} onClick={() => onDirection(option.value)}>{option.label}</button>
          ))}
        </div>
        <div className="dag-viewer__segmented" role="radiogroup" aria-label="Time Compare depth">
          {DEPTH_OPTIONS.map((option) => (
            <button type="button" role="radio" aria-checked={depth === option.value} className={depth === option.value ? "is-active" : ""} key={option.label} onClick={() => onDepth(option.value)}>{option.label}</button>
          ))}
        </div>
        {timeCompareRecordedAsOf && timeCompareSnapshotToken ? (
          <small className="dag-viewer__time-compare-cut">
            One recorded/system cut <time dateTime={timeCompareRecordedAsOf}>{timeCompareRecordedAsOf}</time>
            {" · token "}<code title={timeCompareSnapshotToken}>{abbreviatedSnapshotToken(timeCompareSnapshotToken)}</code>
          </small>
        ) : <small className="dag-viewer__time-compare-cut">Waiting for an exact recorded/system cut and snapshot token.</small>}
        {timeCompareError ? <small className="dag-viewer__time-compare-error" role="alert">{timeCompareError}</small> : null}
        {timeCompareReady ? <small className="dag-viewer__time-compare-ready" role="status">Ready to compare without inferring changes from absence.</small> : null}
      </div>
    );
  }
  if (mode === "runs") {
    return <div className="dag-viewer__context-bar"><p><strong>Runs</strong> Paired family/run IDs align globally when present; otherwise recorded operation identities become honest, family-local histories.</p></div>;
  }
  if (mode === "audit") {
    return <div className="dag-viewer__context-bar"><p><strong>Audit</strong> This is recorded history from available event and timestamp facts—not reconstructed change history.</p></div>;
  }

  const isImpact = mode === "impact";
  return (
    <div className="dag-viewer__context-bar is-focus">
      <p>
        <strong>{isImpact ? "Impact" : "Focus"}</strong>
        {selectedLabel ? <> Anchored on <b>{selectedLabel}</b>.</> : " Select or search for an anchor record."}
        {isImpact && impactSummary ? (
          <>
            {" "}{impactSummary.propagationEnabled} propagation-enabled
            {" · "}{impactSummary.explicitlyInvalidated} explicitly invalidated
            {" · "}{impactSummary.stopped} stopped
            {impactSummary.indeterminate ? <> · {impactSummary.indeterminate} indeterminate</> : null}.
          </>
        ) : !isImpact && resultCount != null ? <> {resultCount} in view.</> : null}
        {isImpact && impactEffectiveAsOf ? <> As of <time dateTime={impactEffectiveAsOf}>{impactEffectiveAsOf}</time>.</> : null}
        {isImpact && impactSummary && impactComplete === false ? <> Bounded result; review stated gaps.</> : null}
        {!isImpact && boundaryCount ? <> {boundaryCount} more beyond this boundary.</> : null}
      </p>
      {!isImpact ? (
        <div className="dag-viewer__segmented" role="radiogroup" aria-label="Lineage direction">
          {DIRECTION_OPTIONS.map((option) => (
            <button type="button" role="radio" aria-checked={direction === option.value} className={direction === option.value ? "is-active" : ""} key={option.value} onClick={() => onDirection(option.value)}>{option.label}</button>
          ))}
        </div>
      ) : null}
      <div className="dag-viewer__segmented" role="radiogroup" aria-label="Lineage depth">
        {DEPTH_OPTIONS.map((option) => (
          <button type="button" role="radio" aria-checked={depth === option.value} className={depth === option.value ? "is-active" : ""} key={option.label} onClick={() => onDepth(option.value)}>{option.label}</button>
        ))}
      </div>
    </div>
  );
}
